import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField
} from '@mui/material';
import { Refresh, ExpandMore } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import api from '../../../services/api/client';

// Helper to format date with time
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm:ss');
  } catch {
    return dateStr;
  }
};

const ENTITY_TYPES = [
  { value: '', label: 'Tous' },
  { value: 'credit_plans', label: 'Plans' },
  { value: 'credit_packs', label: 'Packs' },
  { value: 'credit_costs', label: 'Action Costs' },
  { value: 'promotions', label: 'Promotions' }
];

const ACTION_COLORS = {
  INSERT: 'success',
  UPDATE: 'warning',
  DELETE: 'error'
};

function JsonViewer({ data, title }) {
  if (!data) return <Typography color="text.secondary">-</Typography>;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          bgcolor: 'grey.50',
          maxHeight: 200,
          overflow: 'auto',
          fontFamily: 'monospace',
          fontSize: 12
        }}
      >
        <pre style={{ margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>
      </Paper>
    </Box>
  );
}

export default function AuditLogPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entityType, setEntityType] = useState('');
  const [days, setDays] = useState(30);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (entityType) params.append('entity_type', entityType);
      params.append('days', days.toString());
      params.append('limit', '100');

      const response = await api.get(`/admin/credits/audit-log?${params.toString()}`);
      setLogs(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement des logs');
    } finally {
      setLoading(false);
    }
  }, [entityType, days]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionChip = (actionType) => (
    <Chip
      label={actionType}
      color={ACTION_COLORS[actionType] || 'default'}
      size="small"
    />
  );

  const getEntityLabel = (type) =>
    ENTITY_TYPES.find(e => e.value === type)?.label || type;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Journal d'Audit ({logs.length} entrees)
        </Typography>
        <Button startIcon={<Refresh />} onClick={fetchLogs}>
          Actualiser
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Type d'entite</InputLabel>
              <Select
                value={entityType}
                label="Type d'entite"
                onChange={(e) => setEntityType(e.target.value)}
              >
                {ENTITY_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Jours"
              type="number"
              size="small"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 30)}
              fullWidth
              inputProps={{ min: 1, max: 365 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="outlined" onClick={fetchLogs} fullWidth>
              Appliquer
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {logs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No changes found for this period
          </Typography>
        </Paper>
      ) : (
        <Box>
          {logs.map((log) => (
            <Accordion key={log.id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  {getActionChip(log.action_type)}
                  <Chip
                    label={getEntityLabel(log.entity_type)}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    by <strong>{log.admin_username || 'System'}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(log.created_at)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      ID Entite: {log.entity_id || '-'}
                    </Typography>
                    {log.ip_address && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                        IP : {log.ip_address}
                      </Typography>
                    )}
                  </Grid>
                  {log.action_type === 'UPDATE' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <JsonViewer data={log.old_values} title="Anciennes valeurs" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <JsonViewer data={log.new_values} title="Nouvelles valeurs" />
                      </Grid>
                    </>
                  )}
                  {log.action_type === 'INSERT' && (
                    <Grid item xs={12}>
                      <JsonViewer data={log.new_values} title="Created values" />
                    </Grid>
                  )}
                  {log.action_type === 'DELETE' && (
                    <Grid item xs={12}>
                      <JsonViewer data={log.old_values} title="Deleted values" />
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}
